import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { HttpClient } from '@angular/common/http';
import { ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, TemplateRef } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Content } from 'src/app/models/documents/folder.model';
import { DocumentVersionInfo } from 'src/app/models/documents/document-version.model';
import { SelectableSource } from 'src/app/models/generation/generation.model';
import { PlanFeatureService } from 'src/app/features/plans/services/plan-feature.service';
import { DocumentService } from 'src/app/services/document.service';
import { FrontendLoggerService } from 'src/app/services/frontend-logger.service';
import { LanguageService } from 'src/app/services/language/language.service';
import { NotificationService } from 'src/app/services/notification.service';
import { DriveViewerComposition } from './drive-viewer.composition';
import { DriveItem, ItemType, MoveCallbacks } from './drive-viewer.models';
import { DriveViewerPresentationFacade } from './drive-viewer.presentation-facade';

export { DriveItem, ItemType, MoveCallbacks } from './drive-viewer.models';

@Component({
  selector: 'app-drive-viewer',
  templateUrl: './drive-viewer.component.html',
  styleUrls: [
    './drive-viewer.component.shell-and-tree.scss', './drive-viewer.component.content-and-results.scss',
    './drive-viewer.component.preview-and-dialogs.scss', './drive-viewer.component.overlays-and-states.scss',
    './drive-viewer.component.responsive.scss', './drive-viewer-mobile.component.scss'
  ],
  standalone: false
})
export class DriveViewerComponent implements OnInit, OnChanges, OnDestroy {
  readonly view: DriveViewerPresentationFacade;

  constructor(
    documents: DocumentService,
    public readonly lang: LanguageService,
    dialog: MatDialog,
    sanitizer: DomSanitizer,
    http: HttpClient,
    frontendLogger: FrontendLoggerService,
    router: Router,
    changeDetector: ChangeDetectorRef,
    notifications: NotificationService,
    planFeatures?: PlanFeatureService
  ) {
    this.view = new DriveViewerPresentationFacade(new DriveViewerComposition(
      documents, lang, dialog, sanitizer, http, frontendLogger, router,
      changeDetector, notifications, planFeatures
    ), lang);
  }

  @Input() get active(): boolean { return this.view.active; }
  set active(value: boolean) { this.view.active = value; }
  get items(): DriveItem[] { return this.view.items; }
  set items(value: DriveItem[]) { this.view.items = value; }
  get isRefreshingTree(): boolean { return this.view.isRefreshingTree; }
  set isRefreshingTree(value: boolean) { this.view.isRefreshingTree = value; }
  get isLoadingFiles(): boolean { return this.view.isLoadingFiles; }
  set isLoadingFiles(value: boolean) { this.view.isLoadingFiles = value; }
  get currentFolderId(): string { return this.view.currentFolderId; }
  set currentFolderId(value: string) { this.view.currentFolderId = value; }
  get sortKey() { return this.view.sortKey; }
  set sortKey(value) { this.view.sortKey = value; }
  get sortDir() { return this.view.sortDir; }
  set sortDir(value) { this.view.sortDir = value; }
  get search(): string { return this.view.search; }
  set search(value: string) { this.view.search = value; }
  get filterType(): ItemType | 'all' { return this.view.filterType; }
  set filterType(value: ItemType | 'all') { this.view.filterType = value; }
  get filterOwner() { return this.view.filterOwner; }
  set filterOwner(value) { this.view.filterOwner = value; }
  get selectedIds(): Set<string> { return this.view.selectedIds; }
  get detailsOpen(): boolean { return this.view.detailsOpen; }
  set detailsOpen(value: boolean) { this.view.detailsOpen = value; }
  get mobileTreeOpen(): boolean { return this.view.mobileTreeOpen; }
  set mobileTreeOpen(value: boolean) { this.view.mobileTreeOpen = value; }
  get mobileFiltersOpen(): boolean { return this.view.mobileFiltersOpen; }
  set mobileFiltersOpen(value: boolean) { this.view.mobileFiltersOpen = value; }
  get previewLoadingIds(): Set<string> { return this.view.previewLoadingIds; }
  get previewErrors(): Record<string, string> { return this.view.previewErrors; }
  set previewErrors(value: Record<string, string>) { this.view.previewErrors = value; }
  get contextMenuVisible(): boolean { return this.view.contextMenuVisible; }
  set contextMenuVisible(value: boolean) { this.view.contextMenuVisible = value; }
  get contextMenuMode() { return this.view.contextMenuMode; }
  get contextMenuItem(): DriveItem | undefined { return this.view.contextMenuItem; }
  set contextMenuItem(value: DriveItem | undefined) { this.view.contextMenuItem = value; }
  get moveDialogFiles(): DriveItem[] { return this.view.moveDialogFiles; }
  set moveDialogFiles(value: DriveItem[]) { this.view.moveDialogFiles = value; }
  get deleteDialogName(): string { return this.view.deleteDialogName; }
  get isDraggingFile(): boolean { return this.view.isDraggingFile; }
  set isDraggingFile(value: boolean) { this.view.isDraggingFile = value; }
  get dragHoverFolderId(): string | null { return this.view.dragHoverFolderId; }
  set dragHoverFolderId(value: string | null) { this.view.dragHoverFolderId = value; }
  get summarySources(): SelectableSource[] { return this.view.summarySources; }
  set summarySources(value: SelectableSource[]) { this.view.summarySources = value; }
  get docVersioningEnabled(): boolean { return this.view.docVersioningEnabled; }
  set docVersioningEnabled(value: boolean) { this.view.docVersioningEnabled = value; }
  get versionBadges(): Record<string, number> { return this.view.versionBadges; }
  get versionHistory(): DocumentVersionInfo[] { return this.view.versionHistory; }
  get versionHistoryLoading(): boolean { return this.view.versionHistoryLoading; }
  get versionHistoryError(): string { return this.view.versionHistoryError; }
  get createFolderDialogTpl(): TemplateRef<unknown> | undefined { return this.view.createFolderDialogTpl; }
  set createFolderDialogTpl(value: TemplateRef<unknown> | undefined) { this.view.createFolderDialogTpl = value; }
  get generateSummaryDialogTpl(): TemplateRef<unknown> | undefined { return this.view.generateSummaryDialogTpl; }
  set generateSummaryDialogTpl(value: TemplateRef<unknown> | undefined) { this.view.generateSummaryDialogTpl = value; }
  get versionsDialogTpl(): TemplateRef<unknown> | undefined { return this.view.versionsDialogTpl; }
  set versionsDialogTpl(value: TemplateRef<unknown> | undefined) { this.view.versionsDialogTpl = value; }

  ngOnInit(): void { this.view.initialize(); }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['active']) this.view.activeChanged(this.active);
  }
  ngOnDestroy(): void { this.view.destroy(); }
  translate(key: string): string { return this.view.translate(key); }
  displayName(name: string | null | undefined): string { return this.view.displayName(name); }
  translateMyDrive(): string { return this.view.translateMyDrive(); }
  loadSortPreferences(): void { this.view.loadSortPreferences(); }
  saveSortPreferences(): void { this.view.saveSortPreferences(); }
  onSortKeyChange(value: typeof this.sortKey): void { this.view.onSortKeyChange(value); }
  onSortDirChange(value: typeof this.sortDir): void { this.view.onSortDirChange(value); }
  loadFolderTree(): void { this.view.loadFolderTree(); }
  flattenContentNode(node: Content, parentId: string | null, path: string[]): void { this.view.flattenContentNode(node, parentId, path); }
  inferContentKind(node: Content, children: Content[]): 'folder' | 'file' { return this.view.inferContentKind(node, children); }
  resolveNodeDocumentId(node: Content, fallback: string): string | undefined { return this.view.resolveNodeDocumentId(node, fallback); }
  loadByDateSection(): void { this.view.loadByDateSection(); }
  mergeRootDocuments(): void { this.view.mergeRootDocuments(); }
  reloadAll(): void { this.view.reloadAll(); }
  refreshTreePreservingState(silent = false, finished?: () => void): void { this.view.refreshTreePreservingState(silent, finished); }

  get selectedItem(): DriveItem | undefined { return this.view.selectedItem; }
  get rootFolder(): DriveItem | undefined { return this.view.rootFolder; }
  get currentPath(): string[] { return this.view.currentPath; }
  get visibleItems(): DriveItem[] { return this.view.visibleItems; }
  get currentPreviewItem(): DriveItem | undefined { return this.view.currentPreviewItem; }
  get folderOptions(): DriveItem[] { return this.view.folderOptions; }
  get moveDialogFolderOptions(): DriveItem[] { return this.view.moveDialogFolderOptions; }
  get folderDropListIds(): string[] { return this.view.folderDropListIds; }
  get sourceDropListIds(): string[] { return this.view.sourceDropListIds; }
  isVirtualFolderId(id: string | null | undefined): boolean { return this.view.isVirtualFolderId(id); }
  isUnderByDateItem(item: DriveItem): boolean { return this.view.isUnderByDateItem(item); }
  childTreeItems(parent: string | null): DriveItem[] { return this.view.childTreeItems(parent); }
  childFolders(parent: string | null): DriveItem[] { return this.view.childFolders(parent); }
  trackById(index: number, item: DriveItem): string { return this.view.trackById(index, item); }
  hasChildren(parent: string): boolean { return this.view.hasChildren(parent); }
  dropListId(scope: 'tree' | 'list' | 'grid' | 'mobile', folder: DriveItem): string { return this.view.dropListId(scope, folder); }
  canMoveFileToFolder(file: DriveItem, folderId: string): boolean { return this.view.canMoveFileToFolder(file, folderId); }
  getSelectedFiles(): DriveItem[] { return this.view.getSelectedFiles(); }
  getFileTypeLabel(item: DriveItem): string { return this.view.getFileTypeLabel(item); }

  onDetailsContextMenu(event: MouseEvent): void { this.view.onDetailsContextMenu(event); }
  getSelectedItem(): DriveItem | undefined { return this.view.getSelectedItem(); }
  onContentContextMenu(event: MouseEvent): void { this.view.onContentContextMenu(event); }
  onContentRowContextMenu(event: MouseEvent, item: DriveItem): void { this.view.onContentRowContextMenu(event, item); }
  navigateTo(id: string): void { this.view.navigateTo(id); }
  isExpanded(id: string): boolean { return this.view.isExpanded(id); }
  toggleExpand(id: string, event?: MouseEvent): void { this.view.toggleExpand(id, event); }
  expandPathTo(id: string): void { this.view.expandPathTo(id); }
  breadcrumbClick(index: number): void { this.view.breadcrumbClick(index); }
  toggleSelect(event: MouseEvent, id: string): void { this.view.toggleSelect(event, id); }
  toggleDetails(): void { this.view.toggleDetails(); }
  onTreeItemClick(item: DriveItem, event?: MouseEvent): void { this.view.onTreeItemClick(item, event); }
  onTreeItemDoubleClick(item: DriveItem, event: MouseEvent): void { this.view.onTreeItemDoubleClick(item, event); }
  onItemNameClick(item: DriveItem, event: MouseEvent): void { this.view.onItemNameClick(item, event); }

  openPreview(item: DriveItem): void { this.view.openPreview(item); }
  closePreview(): void { this.view.closePreview(); }
  prevPreview(): void { this.view.prevPreview(); }
  nextPreview(): void { this.view.nextPreview(); }
  isTextPreview(item?: DriveItem): boolean { return this.view.isTextPreview(item); }
  isFramePreview(item?: DriveItem): boolean { return this.view.isFramePreview(item); }
  isPreviewLoading(item?: DriveItem): boolean { return this.view.isPreviewLoading(item); }
  getPreviewError(item?: DriveItem): string | null { return this.view.getPreviewError(item); }
  getPreviewSafeUrl(item?: DriveItem): SafeResourceUrl | null { return this.view.getPreviewSafeUrl(item); }
  getPreviewText(item?: DriveItem): string | null { return this.view.getPreviewText(item); }
  failPreview(item: DriveItem): void { this.view.failPreview(item); }
  getFileIcon(filename: string): string { return this.view.getFileIcon(filename); }
  getIconClass(filename: string): string { return this.view.getIconClass(filename); }
  getItemIcon(item: DriveItem): string { return this.view.getItemIcon(item); }
  getItemIconClass(item: DriveItem): string { return this.view.getItemIconClass(item); }
  mapTypeByExtension(filename: string): ItemType { return this.view.mapTypeByExtension(filename); }
  ensurePreviewUrlForIndex(index: number): void { this.view.ensurePreviewUrlForIndex(index); }
  ensurePreviewUrlForItem(item?: DriveItem): void { this.view.ensurePreviewUrlForItem(item); }
  resolvePreviewUrl(item: DriveItem): void { this.view.resolvePreviewUrl(item); }
  fetchTextContent(url: string, item: DriveItem): void { this.view.fetchTextContent(url, item); }
  fetchPdfBlob(url: string, item: DriveItem): void { this.view.fetchPdfBlob(url, item); }
  buildViewerUrl(item: DriveItem, url: string): string { return this.view.buildViewerUrl(item, url); }
  getExtension(item: DriveItem): string | undefined { return this.view.getExtension(item); }

  onRootContextMenu(event: MouseEvent): void { this.view.onRootContextMenu(event); }
  onTreeContextMenu(event: MouseEvent, item: DriveItem): void { this.view.onTreeContextMenu(event, item); }
  openFolderContextMenu(event: MouseEvent, id: string): void { this.view.openFolderContextMenu(event, id); }
  openFileContextMenu(event: MouseEvent, item: DriveItem): void { this.view.openFileContextMenu(event, item); }
  closeContextMenu(): void { this.view.closeContextMenu(); }
  openCreateFolderDialogFromContext(template: TemplateRef<unknown>): void { this.view.openCreateFolderDialogFromContext(template); }
  openCreateFolderDialogFromMoveFallback(): void { this.view.openCreateFolderDialogFromMoveFallback(); }
  confirmCreateFolder(): void { this.view.confirmCreateFolder(); }
  askAboutContextFile(): void { this.view.askAboutContextFile(); }
  deleteContextFile(template?: TemplateRef<unknown>): void { this.view.deleteContextFile(template); }
  confirmDeleteDialog(): void { this.view.confirmDeleteDialog(); }
  toggleMoveTargets(): void { this.view.toggleMoveTargets(); }
  openMoveDialogFromContext(template: TemplateRef<unknown>): void { this.view.openMoveDialogFromContext(template); }
  moveDialogFilesLabel(): string { return this.view.moveDialogFilesLabel(); }
  moveDialogCreateFolder(): void { this.view.moveDialogCreateFolder(); }
  moveDialogMoveToFolder(folder: DriveItem): void { this.view.moveDialogMoveToFolder(folder); }
  moveContextFilesToFolder(folder: DriveItem): void { this.view.moveContextFilesToFolder(folder); }
  getContextMoveFiles(): DriveItem[] { return this.view.getContextMoveFiles(); }
  resolveDriveItemDocumentId(item?: DriveItem): string | null { return this.view.resolveDriveItemDocumentId(item); }
  confirmDropMove(items: DriveItem[], folder: DriveItem): boolean { return this.view.confirmDropMove(items, folder); }
  moveFilesToFolder(files: DriveItem[], folder: DriveItem, silent = false, confirm = false, callbacks?: MoveCallbacks): boolean {
    return this.view.moveFilesToFolder(files, folder, silent, confirm, callbacks);
  }
  moveKey(fileId: string, folderId: string): string { return this.view.moveKey(fileId, folderId); }
  toDocumentFolderRequestDocumentId(id?: string): string | null { return this.view.toDocumentFolderRequestDocumentId(id); }
  extractCreatedFolderId(created: unknown): number | null { return this.view.extractCreatedFolderId(created); }

  onFileDragStarted(item: DriveItem): void { this.view.onFileDragStarted(item); }
  onFileDragEnded(): void { this.view.onFileDragEnded(); }
  onFolderDragEntered(folder: DriveItem): void { this.view.onFolderDragEntered(folder); }
  onFolderDragExited(folder: DriveItem): void { this.view.onFolderDragExited(folder); }
  isFolderDropCandidate(folder: DriveItem): boolean { return this.view.isFolderDropCandidate(folder); }
  onNativeFileDragStarted(event: DragEvent, item: DriveItem): void { this.view.onNativeFileDragStarted(event, item); }
  onNativeFileDragEnded(): void { this.view.onNativeFileDragEnded(); }
  onNativeFolderDragOver(event: DragEvent, folder: DriveItem): void { this.view.onNativeFolderDragOver(event, folder); }
  onNativeFolderDragLeave(folder: DriveItem): void { this.view.onNativeFolderDragLeave(folder); }
  onNativeDropOnFolder(event: DragEvent, folder: DriveItem): void { this.view.onNativeDropOnFolder(event, folder); }
  canDropNativeFileOnFolder(folder: DriveItem): boolean { return this.view.canDropNativeFileOnFolder(folder); }
  onDropOnFolder(event: CdkDragDrop<unknown>, folder: DriveItem): void { this.view.onDropOnFolder(event, folder); }
  startAutosync(): void { this.view.startAutosync(); }
  stopAutosync(): void { this.view.stopAutosync(); }
  openGenerateSummaryFromContext(): void { this.view.openGenerateSummaryFromContext(); }
  confirmGenerateSummary(): void { this.view.confirmGenerateSummary(); }
  initVersioningFeature(): void { this.view.initVersioningFeature(); }
  showVersionBadge(item: DriveItem): boolean { return this.view.showVersionBadge(item); }
  openVersionHistoryFromContext(): void { this.view.openVersionHistoryFromContext(); }
}
